import { haOpsDashboardReducer } from '@/pages/AccountingCenter/store/haOpsReducer';
import { banksReducer } from '@/pages/Banks/store/banksReducer';
import CompanyCenterReducer from '@/pages/CompanyCenter/store/CompanyCenterSlice';
import complianceReducer from '@/pages/ComplianceCentre/store/complianceCenterReducer';
import dealReducer from '@/pages/Deal/store/dealReducer';
import dealPricingUpdatesReducer from '@/pages/Deal/store/pricingUpdates/reducer';
import dealContingentReducer from '@/pages/DealContingentAnalysis/store/reducer';
import dealsReducer from '@/pages/Deals/store/dealsReducer';
import dealsManagementReducer from '@/pages/DealsManagement/store/dealsManagementSlice';
import debtCentreReducer from '@/pages/DebtCentre/store/debtCentreReducer';
import debtPricerReducer from '@/pages/DebtPricer/store/debtReducer';
import { entityPortfolioReducer } from '@/pages/EntityPortfolio/store/entityPortfolioReducer';
import { entityTradeValuesReducer } from '@/pages/EntityPortfolio/store/entityTradeValuesSlice';
import flCashConsumptionReducer from '@/pages/FLCashConsumption/store/flCashConsumptionReducer';
import fundLevelAdminReducer from '@/pages/FundLevelAdmin/store/fundLevelAdminReducer';
import irAnalysisReducer from '@/pages/IRanalysis/store/reducer';
import marketCurvesCalculationReducer from '@/pages/MarketCurvesCalculation/store/marketCurvesCalculationSlice';
import pricerReducer from '@/pages/Pricers/store/pricer/pricerReducer';
import profileReducer from '@/pages/Profile/store/profileReducer';
import { exposureCalculationReducer } from '@/pages/Rebalancing/components/ExposureCalculation/store/reducer';
import { exposureCaptureReducer } from '@/pages/Rebalancing/components/ExposureCapture/store/reducer';
import scheduledDeliverablesReducer from '@/pages/ScheduledDeliverables/store/scheduledDeliverablesReducer';
import { settlementReducer } from '@/pages/Settlements/store/settlementReducer';
import { sponsorDashboardReducer } from '@/pages/SponsorDashboard/store/sponsorReducer';
import termsAgreementReducer from '@/pages/TermsAgreement/store/termsAgreementReducer';
import bulkDealNameUpdateReducer from '@/pages/TradeCenter/store/bulkDealNameUpdate/bulkDealNameUpdateReducer';
import bulkUploaderReducer from '@/pages/TradeCenter/store/bulkUploader/bulkUploaderReducer';
import tradeCenterReducer from '@/pages/TradeCenter/store/tradeCenterReducer';
import tradeNotificationReducer from '@/pages/TradeNotification/store/reducer';
import { transactionCostAnalysisReducer } from '@/pages/TransactionCostAnalysis/store/transactionCostAnalysisReducer';
import transactionDetailReducer from '@/pages/TransactionDetail/store/transactionDetailReducer';
import { transactionRollingReducer } from '@/pages/TransactionRolling/store/transactionRollingReducer';
import userRoleReducer from '@/pages/UserRole/store/reducer';
import revaluationToolReducer from '@/pages/tools/RevaluationTool/store/revaluationToolReducer';
import usersReducer from '@/pages/users/store/usersReducer';
import fxAnalysisReducer from '@pages/FxAnalysis/store/reducer';
import { pricerPreLoadApi } from '@pages/Pricers/store/pricerPreLoad/pricerPreLoadReducer';
import hairyChartAnalysisReducer from '@pages/RiskManagementAnalysis/HairyChartAnalysis/store/hairyChartSlice';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import appReducer from '@store/app/appReducer';
import companyEntityReducer from '@store/companyEntity/companyEntitySlice';
import dashboardReducer from '@store/dashboard/dashboardReducer';
import marketDataCurvesReducer from '@store/marketData/marketDataSlice';
import modalReducer from '@store/modal/reducer';
import { reportCenterReducer } from '@store/reportCenter/reportCenterReducer';
import { NavigateFunction } from 'react-router-dom';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createTransform,
  persistReducer,
  persistStore,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import debtJournalEntriesReducer from '../pages/DebtJournalEntries/store/reducer';
import gvOpportunitiesReducer from '../pages/GVOpportunities/store/gvOpportunitiesReducer';
import haAdminReducer from '../pages/HAAdmin/store/haAdminSlice';
import hedgeAccountingReducer from '../pages/HedgeAccountingAdmin/store/hedgeAccountingSlice';
import hedgeAccountingReducerV2 from '../pages/HedgeAccountingAdminV2/store/hedgeAccountingSlice';
import journalEntriesReducer from '../pages/JournalEntries/store/journalEntriesSlices';
import { publicHolidayCalendarReducer } from '../pages/PublicHolidayCalendar/store/publicHolidayCalendarReducer';
import runDeliverablesReducer from '../pages/RunDeliverable/store/runDeliverablesSlice';

// defaults to localStorage for web
import authReducer from './auth/authSlice';
import backgroundTasksReducer from './backgroundTasks/backgroundTasksSlice';
import fileManagementReducer from './fileManagement/reducer';
import globalReducer from './global/globalSlice';
import utilityReducer from './utility/utilitySlice';

//we save in persist only those state keys that we need after reloading the page
const filterTransform = createTransform(
  (inboundState, outbound) => {
    if (outbound === 'isAuthenticated' || outbound === 'user')
      return inboundState;
  },
  (outboundState) => outboundState,
);

const apiPersistConfig = {
  key: 'pricerPreLoadApi',
  storage,
  whitelist: ['mutations'],
};

const profilePersistConfig = {
  key: 'profile',
  storage,
  whitelist: ['preferredCurrency'], // only persist preferredCurrency
};

const authPersistConfig = {
  key: 'auth',
  storage,
  transforms: [filterTransform], // Применяем фильтрацию только к auth
};

// Only settledBy — which settle action produced each settlement, needed by
// "Re-settle" to pick the corresponding netting rule. Deliberately not the rest
// of the slice: it holds large trade and history arrays.
const settlementPersistConfig = {
  key: 'settlement',
  storage,
  whitelist: ['settledBy'],
};

export const combinedReducers = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  profile: persistReducer(profilePersistConfig, profileReducer),
  utility: utilityReducer,
  global: globalReducer,
  marketData: marketCurvesCalculationReducer,
  transactionDetail: transactionDetailReducer,
  marketDataCurves: marketDataCurvesReducer,
  hairyChartAnalysis: hairyChartAnalysisReducer,
  settlement: persistReducer(settlementPersistConfig, settlementReducer),
  sponsor: sponsorDashboardReducer,
  haOps: haOpsDashboardReducer,
  app: appReducer,
  banks: banksReducer,
  modal: modalReducer,
  termsAgreement: termsAgreementReducer,
  tradeCenter: tradeCenterReducer,
  users: usersReducer,
  reportCenter: reportCenterReducer,
  dashboard: dashboardReducer,
  journalEntries: journalEntriesReducer,
  haAdmin: haAdminReducer,
  hedgeAccounting: hedgeAccountingReducer,
  hedgeAccountingV2: hedgeAccountingReducerV2,
  runDeliverables: runDeliverablesReducer,
  scheduledDeliverables: scheduledDeliverablesReducer,
  companyEntity: companyEntityReducer,
  revalueTrades: revaluationToolReducer,
  backgroundTasks: backgroundTasksReducer,
  pricerPreLoadApi: persistReducer(apiPersistConfig, pricerPreLoadApi.reducer),
  pricer: pricerReducer,
  debtPricer: debtPricerReducer,
  companyCenter: CompanyCenterReducer,
  tradeNotification: tradeNotificationReducer,
  transactionRolling: transactionRollingReducer,
  entityPortfolio: entityPortfolioReducer,
  bulkTradeUploader: bulkUploaderReducer,
  bulkDealNameUpdate: bulkDealNameUpdateReducer,
  deals: dealsReducer,
  deal: dealReducer,
  dealPricingUpdates: dealPricingUpdatesReducer,
  irAnalysis: irAnalysisReducer,
  fxAnalysis: fxAnalysisReducer,
  flCashConsumption: flCashConsumptionReducer,
  fundLevelAdmin: fundLevelAdminReducer,
  complianceFL: complianceReducer,
  fileManagement: fileManagementReducer,
  dealContingent: dealContingentReducer,
  exposureCapture: exposureCaptureReducer,
  exposureCalculation: exposureCalculationReducer,
  entityTradeValues: entityTradeValuesReducer,
  debtCentre: debtCentreReducer,
  debtJournalEntries: debtJournalEntriesReducer,
  transactionCostAnalysis: transactionCostAnalysisReducer,
  dealsManagement: dealsManagementReducer,
  userRole: userRoleReducer,
  gvOpportunities: gvOpportunitiesReducer,
  publicHolidayCalendar: publicHolidayCalendarReducer,
});

export const rootReducer: typeof combinedReducers = (state, action) => {
  if (action.type === 'auth/logout/fulfilled') {
    return combinedReducers(undefined, action);
  }

  if (action.type === PURGE) {
    return combinedReducers(undefined, action);
  }

  return combinedReducers(state, action);
};

export const setupStore = (navigate: NavigateFunction) => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        // Disable immutability check for large state slices that cause performance issues
        immutableCheck: {
          // Ignore these state paths - they contain large arrays that slow down dev mode
          ignoredPaths: [
            'tradeNotification.approval.data',
            'tradeNotification.executable.data',
            'tradeNotification.postExecution.data',
            'tradeNotification.cancelled.data',
            'tradeNotification.mismatch.data',
            'tradeNotification.executed.data',
            'tradeNotification.fixReference.data',
            'tradeCenter.trades',
            'companyCenter.companyCenterList',
            'companyCenter.sponsorList',
            'companyCenter.companyList',
            // Settlement lists are flow-level and deeply nested (reports →
            // settlements → trades → flows); scanning them cost 10s+ per action.
            'settlement.transactionSettlement.data',
            'settlement.historySettlement.data',
            'settlement.allSettlements.data',
          ],
          // Increase warning threshold
          warnAfter: 128,
        },
        serializableCheck: {
          // Redux Persist actions must be ignored
          ignoredActions: [
            FLUSH,
            REHYDRATE,
            PAUSE,
            PERSIST,
            PURGE,
            REGISTER,
            'tradeCenter/fetchTrades/fulfilled',
            'settlement/getTransactionSettlement/success',
            'settlement/getSettlementHistory/success',
            'settlement/getAllSettlements/success',
            'settlement/setAggregatedSettlement/success',
            'settlement/fetchTransactionSettlement/fulfilled',
            'settlement/fetchSettlementHistory/fulfilled',
            'settlement/fetchAllSettlements/fulfilled',
            'settlement/setAggregatedSettlements/fulfilled',
          ],
          // Ignore these field paths in all actions
          ignoredPaths: [
            'tradeCenter.trades',
            'tradeNotification.approval.data',
            'tradeNotification.executable.data',
            'tradeNotification.postExecution.data',
            'tradeNotification.cancelled.data',
            'tradeNotification.mismatch.data',
            'tradeNotification.executed.data',
            'companyCenter.companyCenterList',
            'companyCenter.sponsorList',
            'companyCenter.companyList',
            'settlement.transactionSettlement.data',
            'settlement.historySettlement.data',
            'settlement.allSettlements.data',
          ],
          // Increase the timing threshold in development
          warnAfter: 1000,
        },
        thunk: {
          extraArgument: { navigate },
        },
      }).concat(pricerPreLoadApi.middleware),
  });
};

export const persistor = (store: ReturnType<typeof setupStore>) =>
  persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = ReturnType<typeof setupStore>['dispatch'];
